import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { getStoreItems } from 'src/redux/actions/store';
import type { ProRecurringTerms } from 'src/helpers/subscription';
import { proRecurringTerms } from 'src/helpers/subscription';
import { useLiveOffer } from 'src/components/careerProPitch/OfferCountdown';
import {
  PRO_PLUS,
  PRO_TIERS,
  type ProTier,
  type ProTierType,
  proTierOf,
} from 'src/components/careerProPitch/tiers';
import { useProPitchCopy } from 'src/components/careerProPitch/variants';
import type { GetStoreItemsSub } from 'src/openapi';

/**
 * The Abekus Pro item currently on sale, and its recurring terms.
 *
 * Loads the catalogue itself rather than assuming a caller did: the course path
 * pitches Pro from a screen that never touches the store, and reading an
 * empty `shopItems` there produced `isKnown: false` terms that would otherwise
 * have been printed as "no auto-renew" on a plan that renews.
 */
// One store read for every Pro surface mounting on an empty store at once: the
// top bar alone mounts three (the window chip twice, Go Pro).
let storeLoad: Promise<unknown> | null = null;

export const useProItem = () => {
  const dispatch = useAppDispatch();
  const shopItemsCount = useAppSelector(
    (state) => state.store.shopItems.length,
  );
  const premium = useAppSelector((state) =>
    state.store.shopItems.find(
      (item) => item.item_type === 'premium_profile_standard',
    ),
  );

  useEffect(() => {
    if (!shopItemsCount && !storeLoad) {
      storeLoad = dispatch(getStoreItems()).finally(() => {
        storeLoad = null;
      });
    }
  }, [dispatch, shopItemsCount]);

  const shown = useLiveOffer(premium);

  return { premium: shown, terms: proRecurringTerms(shown) };
};

/** Each Pro tier on sale on the web now, cheapest first, as the store lists
 * it (the store loads through useProItem). */
export const useProTierItems = (): {
  tier: ProTier;
  item: GetStoreItemsSub;
}[] => {
  const shopItems = useAppSelector((state) => state.store.shopItems);
  return PRO_TIERS.flatMap((tier) => {
    const item = shopItems.find((i) => i.item_type === tier.type);
    return item ? [{ tier, item }] : [];
  });
};

/**
 * Every tier a Pro surface can sell -- Pro+ as `proPlusItem` priced it, the
 * others as the store lists them -- and the one it leads with: the tiers
 * test's `leadTier` while that tier is on sale, else Pro+. `onlyWithCall`
 * keeps the tiers with the monthly call (a wall that sells the call).
 */
export const useProTierOptions = (
  proPlusItem: GetStoreItemsSub | null | undefined,
  leadTier: ProTierType,
  onlyWithCall = false,
) => {
  const options = useProTierItems()
    .filter((option) => !onlyWithCall || option.tier.monthlyCall)
    .map((option) =>
      option.tier.type === PRO_PLUS && proPlusItem
        ? { ...option, item: proPlusItem }
        : option,
    );
  if (proPlusItem && !options.some((o) => o.tier.type === PRO_PLUS)) {
    options.push({ tier: proTierOf(PRO_PLUS), item: proPlusItem });
  }
  const leading = options.some((o) => o.tier.type === leadTier)
    ? leadTier
    : PRO_PLUS;
  return { options, leading };
};

/**
 * The tier a Pro surface with no tier picker sells (the full-page offer):
 * this user's lead tier in the tiers test, its item as it may be shown now
 * and that item's terms, and the arm to report its events under (see
 * useProPitchCopy) once settled.
 */
export const useLeadTierOffer = (
  proPlusItem: GetStoreItemsSub | null | undefined,
) => {
  const { leadTier, renderedArm, settled } = useProPitchCopy();
  const { options, leading } = useProTierOptions(proPlusItem, leadTier);
  const item = useLiveOffer(
    options.find((o) => o.tier.type === leading)?.item ?? proPlusItem,
  );
  return {
    tier: proTierOf(leading),
    item,
    terms: proRecurringTerms(item),
    renderedArm,
    settled,
  };
};

/** Just the terms, for a caller that never prices or buys the item. */
const useProTerms = (): ProRecurringTerms => useProItem().terms;

export default useProTerms;
